'use client';

import { useShowMoreLess } from '@/hooks/useShowMoreLess';
import { cn } from '@/lib/utils';

import { BlockHeader, BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { BlockWrapper } from '@/cms/BlockWrapperDC';
import { BlockComponentModel } from '@/cms/types/blok-component';
import Button, { ButtonProps } from '@/components/atoms/Button';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';
import { Text } from '@/components/atoms/Text';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import { passButtonIcons } from '../../atoms/Button/utils';

interface TextGridStrapiProps {
  header?: BlockHeader;
  items: {
    image?: ImageStrapiProps;
    title: string;
    titleTag?: 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    description: RichTextFieldStrapiProps;
    link?: LinkButtonStrapiProps;
  }[];
  layout: 'two-columns' | 'three-columns';
  isShowMoreVisible: boolean;
}

export function TextGridStrapi({
  block,
  ...rest
}: BlockComponentModel<TextGridStrapiProps>) {
  const { items, header, layout, isShowMoreVisible } = block;

  let initialShowCount = Infinity;
  if (isShowMoreVisible) {
    if (layout === 'two-columns') {
      initialShowCount = 4;
    } else {
      initialShowCount = 6;
    }
  }

  const { toggleShowMore, initiallyVisibleItems, buttonText, showAction } =
    useShowMoreLess({
      items,
      initialShowCount,
    });
  const cardsClasses = {
    'two-columns': 'grid grid-cols-1 md:grid-cols-2',
    'three-columns': 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  };

  return (
    <BlockWrapper {...rest} data-tracker-section={SECTION_NAMES.TEXT_GRID}>
      <div>
        <BlockHeaderContent
          {...header}
          layoutOptions={{ constrainWidthWithPadding: true }}
          renderButtons={
            showAction
              ? [
                  (props: Partial<ButtonProps>) => (
                    <Button
                      onClick={toggleShowMore}
                      key={buttonText + 'text-grid-strapi'}
                      variant={props.variant || 'outlined'}
                      color={props.color || 'secondary'}
                      {...passButtonIcons(props)}
                    >
                      {buttonText}
                    </Button>
                  ),
                ]
              : undefined
          }
          classNames={{
            children: cn(
              'gap-8 md:gap-[3.75rem] lg:gap-20 md:items-start items-start',
              cardsClasses[layout]
            ),
          }}
        >
          {items.map((item, index) => (
            <div
              className={cn(
                'text-left',
                initiallyVisibleItems?.length &&
                  !initiallyVisibleItems.some(
                    (card) => card.title === item.title
                  ) &&
                  'hide-content'
              )}
              key={index}
            >
              {item.image && (
                <div className='mb-6'>
                  <ImageStrapi
                    block={item.image}
                    inner={{ classNames: { image: 'w-9' } }}
                  />
                </div>
              )}
              <Text
                as={item.titleTag}
                variant='subtitle-l-d'
                color='base-black'
                classNames={{ wrapper: 'mb-6 font-serif' }}
              >
                {item.title}
              </Text>
              <div className='flex flex-wrap gap-2 text-gray-500'>
                <div data-tracker-component='description'>
                  <RichTextFieldStrapi block={item.description} />
                </div>
                {item.link && (
                  <LinkButtonStrapi
                    data-tracker-component='learn-more'
                    block={item.link}
                    inner={{
                      variant: 'text',
                      slim: true,
                      classNames: { root: 'text-brand-500' },
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
}
